import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Field, Input, Spinner, useToast } from '../../ui/index.js';

/**
 * Platform tab → Mail reading (059).
 *
 * Which Entra application reads the desks' mailboxes, and whether it can
 * read each one. The same registration the n8n desk used — it already has
 * Mail.Read on the desk mailboxes. Which mailboxes are READ is each desk's
 * choice, on its own Mail tab; this is only whether they CAN be.
 */

type Source = 'platform' | 'environment' | 'unset';
interface FieldValue {
  value: string;
  source: Source;
}
interface Payload {
  tenantId: FieldValue;
  clientId: FieldValue;
  clientSecretSet: boolean;
  clientSecretSource: Source;
  enabled: boolean;
  mailboxes: {
    tenantId: string;
    group: string;
    address: string | null;
    mode: 'off' | 'shadow' | 'on';
    lastOkAt: string | null;
    lastError: string | null;
  }[];
}

const where = (s: Source) => (s === 'platform' ? 'Set here' : s === 'environment' ? 'From the environment' : 'Not set');
const MODE: Record<string, { label: string; tone: 'ok' | 'info' | 'neutral' }> = {
  on: { label: 'reading', tone: 'ok' },
  shadow: { label: 'shadow', tone: 'info' },
  off: { label: 'off', tone: 'neutral' },
};

function CheckButton({ address, disabled }: { address: string; disabled: boolean }) {
  const [result, setResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const check = useMutation({
    mutationFn: () => api.post<{ ok: boolean; detail: string }>('/api/v1/platform/mail-reading/check', { address }),
    onSuccess: (r) => setResult(r),
    onError: (e) => setResult({ ok: false, detail: e instanceof ApiError ? e.message : 'The check could not run.' }),
  });
  return (
    <div className="flex min-w-0 flex-col items-end gap-1">
      <Button size="xs" variant="outline" onClick={() => check.mutate()} loading={check.isPending} disabled={disabled}>
        Check access
      </Button>
      {result && (
        <span className={`flex items-start gap-1 text-3xs ${result.ok ? 'text-ok' : 'text-danger'}`}>
          {result.ok ? <CheckCircle2 aria-hidden className="h-3 w-3 shrink-0" /> : <XCircle aria-hidden className="h-3 w-3 shrink-0" />}
          <span className="text-right">{result.detail}</span>
        </span>
      )}
    </div>
  );
}

export function MailReadingPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['platform-mail-reading'],
    queryFn: () => api.get<Payload>('/api/v1/platform/mail-reading'),
  });
  const [draft, setDraft] = useState<{ tenantId?: string; clientId?: string; clientSecret?: string }>({});
  const save = useMutation({
    mutationFn: (body: typeof draft) => api.put('/api/v1/platform/mail-reading', body),
    onSuccess: () => {
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['platform-mail-reading'] });
      toast.success('Mail reading saved');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <p className="text-xs text-ink-tertiary">Mail reading could not be loaded.</p>;
  const s = q.data;
  const dirty = Object.keys(draft).length > 0;
  const val = (k: 'tenantId' | 'clientId') => draft[k] ?? s[k].value;

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <header className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Mail reading</h3>
          <p className="text-2xs text-ink-tertiary">
            The Entra application that reads desk mailboxes through Microsoft Graph. Applies to{' '}
            <strong className="text-ink-secondary">every group</strong>.
          </p>
        </div>
        <Badge tone={s.enabled ? 'ok' : 'neutral'}>{s.enabled ? 'Configured' : 'Not configured'}</Badge>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Directory (tenant) ID" hint={where(s.tenantId.source)}>
          <Input value={val('tenantId')} onChange={(e) => setDraft({ ...draft, tenantId: e.target.value })} />
        </Field>
        <Field label="Application (client) ID" hint={where(s.clientId.source)}>
          <Input value={val('clientId')} onChange={(e) => setDraft({ ...draft, clientId: e.target.value })} />
        </Field>
        <Field label="Client secret" hint={s.clientSecretSet ? 'Set — leave blank to keep it' : 'Not set'}>
          <Input
            type="password"
            value={draft.clientSecret ?? ''}
            placeholder={s.clientSecretSet ? '••••••••' : ''}
            onChange={(e) => setDraft({ ...draft, clientSecret: e.target.value })}
          />
        </Field>
      </div>

      <p className="mt-3 text-2xs leading-relaxed text-ink-tertiary">
        Needs the <strong className="text-ink-secondary">Mail.Read</strong> application permission with admin
        consent, and <strong className="text-ink-secondary">Mail.Send</strong> when Outbound mail sends through
        Microsoft Graph. Reading writes nothing to a mailbox: messages are not marked read or moved. If mailbox access
        is restricted, each desk mailbox must be in the allowed group.
      </p>

      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          onClick={() => {
            // A blank secret box means "keep it", not "clear it": the server
            // reads an empty string as "clear", so it is left out entirely.
            const body = { ...draft };
            if (body.clientSecret !== undefined && body.clientSecret.trim() === '') delete body.clientSecret;
            save.mutate(body);
          }}
          disabled={!dirty}
          loading={save.isPending}
        >
          Save
        </Button>
      </div>

      <h4 className="mb-2 mt-4 text-xs font-semibold text-ink">Desk mailboxes</h4>
      <ul className="space-y-1.5">
        {s.mailboxes.map((m) => (
          <li
            key={m.tenantId}
            className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-line-subtle p-2.5 text-2xs"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink">{m.group}</span>
                <Badge tone={MODE[m.mode]!.tone}>{MODE[m.mode]!.label}</Badge>
              </div>
              <div className="mt-0.5 font-mono text-ink-secondary">{m.address ?? 'no desk mailbox set'}</div>
              {m.lastError && <div className="mt-0.5 text-danger">{m.lastError}</div>}
              {!m.lastError && m.lastOkAt && (
                <div className="mt-0.5 text-ink-tertiary">Last read {new Date(m.lastOkAt).toLocaleString()}</div>
              )}
            </div>
            {m.address && <CheckButton address={m.address} disabled={!s.enabled || dirty} />}
          </li>
        ))}
      </ul>
    </section>
  );
}
